import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, CartesianGrid, Legend,
} from "recharts";
import {
  Download, BarChart3, TrendingUp, AlertTriangle, Star, Clock,
  IndianRupee,
} from "lucide-react";

const COLORS = ["#BE1019", "#105330", "#A7744A", "#888780", "#D2AD78", "#8FC3A3", "#E0989D", "#C29300"];

const TYPE_LABEL = {
  it_support: "IT Support", stationary: "Stationary",
  facility: "Facility", hr_request: "HR Request",
};

const STATUS_LABEL = (s) => (s || "").replace(/_/g, " ");

export default function RequestAnalytics() {
  const [data, setData] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [type, setType] = useState("");

  const refresh = async () => {
    const params = new URLSearchParams();
    if (dateFrom) params.append("date_from", dateFrom);
    if (dateTo) params.append("date_to", dateTo);
    if (type) params.append("request_type", type);
    try {
      const { data: d } = await api.get(`/request-analytics?${params.toString()}`);
      setData(d);
    } catch (e) { console.error("[RequestAnalytics] refresh failed", e); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [dateFrom, dateTo, type]);

  const download = () => {
    const params = new URLSearchParams();
    if (dateFrom) params.append("date_from", dateFrom);
    if (dateTo) params.append("date_to", dateTo);
    if (type) params.append("request_type", type);
    window.open(`${api.defaults.baseURL}/request-export?${params.toString()}`, "_blank");
  };

  if (!data) return <div className="p-8 text-sm text-slate-500">Loading…</div>;

  const typeData = Object.entries(data.volume_by_type || {}).map(([k, v]) => ({ name: TYPE_LABEL[k] || k, value: v }));
  const statusData = Object.entries(data.volume_by_status || {}).map(([k, v]) => ({ name: STATUS_LABEL(k), value: v }));
  const priorityData = Object.entries(data.volume_by_priority || {}).map(([k, v]) => ({ name: k, value: v }));
  const dailyData = data.daily_volume || [];
  const costData = Object.entries(data.cost_by_bucket || {}).map(([k, v]) => ({ name: k.replace(/_/g, " "), value: v }));
  const satDist = Object.entries(data.satisfaction_distribution || {}).map(([k, v]) => ({ rating: `${k}★`, count: v }));

  return (
    <div className="space-y-5" data-testid="request-analytics">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Analytics</div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Service Request Analytics
          </h1>
          <p className="text-sm text-slate-500 mt-1">Volume, approvals, satisfaction, costs and SLA breaches.</p>
        </div>
        <Button onClick={download} className="brand-bg brand-bg-hover text-white" data-testid="export-csv-btn">
          <Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 bg-white border border-slate-200 rounded-lg p-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 block">From</label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-7 text-xs" data-testid="filter-from" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 block">To</label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-7 text-xs" data-testid="filter-to" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 block">Type</label>
          <Select value={type || "_all"} onValueChange={(v) => setType(v === "_all" ? "" : v)}>
            <SelectTrigger className="w-40 h-7 text-xs" data-testid="filter-type"><SelectValue placeholder="All types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All types</SelectItem>
              <SelectItem value="it_support">IT Support</SelectItem>
              <SelectItem value="stationary">Stationary</SelectItem>
              <SelectItem value="facility">Facility</SelectItem>
              <SelectItem value="hr_request">HR Request</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPI Tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Kpi label="Total" value={data.total || 0} icon={BarChart3} accent="slate" testid="kpi-total" />
        <Kpi label="SLA Breached" value={data.sla_breached || 0} icon={AlertTriangle} accent="red" testid="kpi-sla-breached" />
        <Kpi label="Avg Resolution" value={data.avg_resolution_hours != null ? `${data.avg_resolution_hours}h` : "—"} icon={Clock} accent="blue" testid="kpi-avg-resolution" />
        <Kpi label="Avg Approval" value={data.avg_approval_hours != null ? `${data.avg_approval_hours}h` : "—"} icon={TrendingUp} accent="amber" testid="kpi-avg-approval" />
        <Kpi label="Satisfaction" value={data.satisfaction_avg != null ? `${data.satisfaction_avg}/5` : "—"} icon={Star} accent="emerald" testid="kpi-satisfaction" />
        <Kpi label="Est. Cost ₹" value={data.total_estimated_cost ? `${(data.total_estimated_cost / 1000).toFixed(0)}k` : "—"} icon={IndianRupee} accent="brand" testid="kpi-cost" />
      </div>

      {/* Charts row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <ChartCard title="Volume by Type" testid="chart-by-type">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={typeData}>
              <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="value" fill="#AF3C3C" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Status Distribution" testid="chart-by-status">
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                {statusData.map((_, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
              </Pie>
              <Tooltip />
              <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: 10 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Charts row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <ChartCard title="Daily Volume" testid="chart-daily">
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={dailyData}>
              <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="count" stroke="#0F766E" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Cost by Approval Bucket" testid="chart-cost">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={costData} layout="vertical">
              <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={130} />
              <Tooltip formatter={(v) => `₹${Number(v).toLocaleString("en-IN")}`} />
              <Bar dataKey="value" fill="#A16207" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Charts row 3 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <ChartCard title="Priority Mix" testid="chart-priority">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={priorityData}>
              <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="value" fill="#1E3A8A" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title={`Satisfaction (${data.satisfaction_count || 0} ratings)`} testid="chart-satisfaction">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={satDist}>
              <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
              <XAxis dataKey="rating" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#0F766E" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Top requesters */}
      {data.top_requesters && data.top_requesters.length > 0 && (
        <ChartCard title="Top Requesters" testid="chart-top-requesters">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500">
              <tr><th className="text-left py-1">Rank</th><th className="text-left py-1">Employee</th><th className="text-right py-1">Requests</th></tr>
            </thead>
            <tbody>
              {data.top_requesters.map((r, idx) => (
                <tr key={idx} className="border-t border-slate-100">
                  <td className="py-1 text-slate-500">#{idx + 1}</td>
                  <td className="py-1 font-medium text-slate-900">{r.name}</td>
                  <td className="py-1 text-right font-mono">{r.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ChartCard>
      )}
    </div>
  );
}

function Kpi({ label, value, icon: Icon, accent = "slate", testid }) {
  const tone = {
    slate: "border-slate-100 bg-slate-50/40 text-slate-700",
    red: "border-red-100 bg-red-50/40 text-red-700",
    blue: "border-blue-100 bg-blue-50/40 text-blue-700",
    amber: "border-amber-100 bg-amber-50/40 text-amber-700",
    emerald: "border-emerald-100 bg-emerald-50/40 text-emerald-700",
    brand: "border-red-100 bg-red-50/40 brand-text",
  }[accent];
  return (
    <div className={`rounded-md border ${tone} p-3`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <Icon className="h-3.5 w-3.5" />
        <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      </div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}

function ChartCard({ title, testid, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid={testid}>
      <div className="text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wider">{title}</div>
      {children}
    </div>
  );
}
